/**
 * MatchupAnalyzerModal - Deep-dive matchup comparison modal.
 *
 * Shows BPI comparison, seed history, paper tiger warnings,
 * key factors, and projected scores when user clicks "Analyze"
 * on any bracket matchup.
 */

import { useState, useEffect, useRef } from 'react';
import { analyzeMatchup } from '../../services/bracketService';
import api from '../../services/api';

function MatchupAnalyzerModal({ team1EntryId, team2EntryId, roundNumber, autoKnewton = false, onClose, onPickWinner }) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [knewtonResponse, setKnewtonResponse] = useState(null);
  const [knewtonLoading, setKnewtonLoading] = useState(false);
  const knewtonTriggered = useRef(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const data = await analyzeMatchup(team1EntryId, team2EntryId, roundNumber);
        setAnalysis(data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load analysis');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [team1EntryId, team2EntryId, roundNumber]);

  // Auto-trigger Knewton analysis when opened via "Knewton" button
  useEffect(() => {
    if (autoKnewton && analysis && !knewtonTriggered.current && !knewtonResponse && !knewtonLoading) {
      knewtonTriggered.current = true;
      const t1 = analysis.team1;
      const t2 = analysis.team2;
      (async () => {
        setKnewtonLoading(true);
        try {
          const query = `Analyze this March Madness bracket matchup: #${t1.seed} ${t1.team_name} (${t1.region}) vs #${t2.seed} ${t2.team_name} (${t2.region}) in the ${analysis.round_name}. Use get_bracket_matchup to compare their BPI, strength of schedule, momentum, and quality wins. Who should I pick and why? Give me a confident recommendation.`;
          const response = await api.post('/chat', { query, sport: 'ncaa_b' });
          setKnewtonResponse(response.data.message);
        } catch {
          setKnewtonResponse('Unable to get Knewton analysis right now. Try again later.');
        } finally {
          setKnewtonLoading(false);
        }
      })();
    }
  }, [autoKnewton, analysis, knewtonResponse, knewtonLoading]);

  if (loading) {
    return (
      <ModalShell onClose={onClose}>
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin w-8 h-8 border-2 border-accent-blue border-t-transparent rounded-full" />
        </div>
      </ModalShell>
    );
  }

  if (error) {
    return (
      <ModalShell onClose={onClose}>
        <div className="text-center py-12">
          <p className="text-danger text-sm">{error}</p>
        </div>
      </ModalShell>
    );
  }

  const { team1, team2 } = analysis;

  return (
    <ModalShell onClose={onClose}>
      {/* Header */}
      <div className="text-center mb-4">
        <div className="text-[10px] text-text-tertiary tracking-wider uppercase mb-1">
          {analysis.round_name}
        </div>
        <div className="flex items-center justify-center gap-4">
          <TeamBadge team={team1} />
          <span className="text-lg font-bold text-text-tertiary">vs</span>
          <TeamBadge team={team2} />
        </div>
      </div>

      {/* BPI Comparison */}
      {analysis.bpi_differential != null && (
        <div className="glass-card p-3 mb-3">
          <div className="text-[10px] text-text-tertiary uppercase tracking-wider mb-2">BPI Comparison</div>
          <div className="flex items-center justify-between">
            <div className="text-center flex-1">
              <div className={`text-xl font-bold ${analysis.bpi_advantage === team1.team_name ? 'text-accent-blue' : 'text-text-secondary'}`}>
                {team1.bpi?.toFixed(1) ?? '---'}
              </div>
              <div className="text-[10px] text-text-tertiary">{team1.team_name}</div>
            </div>
            <div className="px-3">
              <div className="text-xs font-bold text-accent-blue">
                +{Math.abs(analysis.bpi_differential).toFixed(1)}
              </div>
              <div className="text-[9px] text-text-tertiary">{analysis.bpi_advantage}</div>
            </div>
            <div className="text-center flex-1">
              <div className={`text-xl font-bold ${analysis.bpi_advantage === team2.team_name ? 'text-accent-blue' : 'text-text-secondary'}`}>
                {team2.bpi?.toFixed(1) ?? '---'}
              </div>
              <div className="text-[10px] text-text-tertiary">{team2.team_name}</div>
            </div>
          </div>
        </div>
      )}

      {/* Tempo / Pace */}
      {analysis.projected_pace && (
        <div className="glass-card p-3 mb-3">
          <div className="text-[10px] text-text-tertiary uppercase tracking-wider mb-1">Tempo Projection</div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-text-primary">{analysis.tempo_description}</span>
            <span className="text-sm font-bold text-accent-blue">{analysis.projected_pace} poss/g</span>
          </div>
        </div>
      )}

      {/* Seed History */}
      {analysis.seed_history && (
        <div className="glass-card p-3 mb-3">
          <div className="text-[10px] text-text-tertiary uppercase tracking-wider mb-2">
            Historical: #{analysis.seed_history.higher_seed} vs #{analysis.seed_history.lower_seed}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-text-secondary">Higher seed wins</span>
                <span className="font-bold text-text-primary">{analysis.seed_history.higher_seed_wins}</span>
              </div>
              <div className="w-full h-2 bg-bg-primary rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-success to-accent-blue rounded-full"
                  style={{ width: `${(1 - analysis.seed_history.upset_rate) * 100}%` }}
                />
              </div>
            </div>
            <div className="text-center px-2">
              <div className="text-lg font-bold text-warning">
                {(analysis.seed_history.upset_rate * 100).toFixed(0)}%
              </div>
              <div className="text-[9px] text-text-tertiary">Upset Rate</div>
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-text-secondary">Upset wins</span>
                <span className="font-bold text-text-primary">{analysis.seed_history.lower_seed_wins}</span>
              </div>
              <div className="w-full h-2 bg-bg-primary rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-danger to-orange-400 rounded-full"
                  style={{ width: `${analysis.seed_history.upset_rate * 100}%` }}
                />
              </div>
            </div>
          </div>
          <div className="text-[10px] text-text-tertiary text-center mt-1">
            {analysis.seed_history.total_matchups} historical matchups
          </div>
        </div>
      )}

      {/* Paper Tiger Warnings */}
      {analysis.paper_tiger_warnings?.length > 0 && (
        <div className="space-y-1.5 mb-3">
          {analysis.paper_tiger_warnings.map((w, i) => (
            <div
              key={i}
              className={`px-3 py-2 rounded-lg text-xs ${
                w.severity === 'danger'
                  ? 'bg-danger/10 border border-danger/30 text-danger'
                  : 'bg-warning/10 border border-warning/30 text-warning'
              }`}
            >
              <span className="font-bold">{w.team_name}:</span> {w.reason}
            </div>
          ))}
        </div>
      )}

      {/* Key Factors */}
      {analysis.key_factors?.length > 0 && (
        <div className="glass-card p-3 mb-3">
          <div className="text-[10px] text-text-tertiary uppercase tracking-wider mb-2">Key Factors</div>
          <ul className="space-y-1.5">
            {analysis.key_factors.map((factor, i) => (
              <li key={i} className="flex gap-2 text-xs text-text-secondary">
                <span className="text-accent-blue shrink-0 mt-0.5">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </span>
                {factor}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Stats Comparison Table */}
      <div className="glass-card p-3 mb-3">
        <div className="text-[10px] text-text-tertiary uppercase tracking-wider mb-2">Advanced Stats</div>
        <div className="space-y-1">
          <StatRow label="SOS Rank" val1={team1.sos_rank && `#${team1.sos_rank}`} val2={team2.sos_rank && `#${team2.sos_rank}`} lowerBetter />
          <StatRow label="Net Rating" val1={team1.net_rating?.toFixed(1)} val2={team2.net_rating?.toFixed(1)} />
          <StatRow label="eFG%" val1={team1.effective_fg_pct && `${(team1.effective_fg_pct * 100).toFixed(1)}%`} val2={team2.effective_fg_pct && `${(team2.effective_fg_pct * 100).toFixed(1)}%`} />
          <StatRow label="TO%" val1={team1.turnover_pct && `${team1.turnover_pct.toFixed(1)}%`} val2={team2.turnover_pct && `${team2.turnover_pct.toFixed(1)}%`} lowerBetter />
          <StatRow label="Quality W" val1={team1.quality_wins} val2={team2.quality_wins} />
          <StatRow label="Streak" val1={formatStreak(team1)} val2={formatStreak(team2)} />
          <StatRow label="Last 5" val1={team1.last_5_record} val2={team2.last_5_record} />
        </div>
      </div>

      {/* Ask Knewton */}
      <div className="mb-3">
        {!knewtonResponse && !knewtonLoading && (
          <button
            onClick={async () => {
              setKnewtonLoading(true);
              try {
                const query = `Analyze this March Madness bracket matchup: #${team1.seed} ${team1.team_name} (${team1.region}) vs #${team2.seed} ${team2.team_name} (${team2.region}) in the ${analysis.round_name}. Use get_bracket_matchup to compare their BPI, strength of schedule, momentum, and quality wins. Who should I pick and why? Give me a confident recommendation.`;
                const response = await api.post('/chat', { query, sport: 'ncaa_b' });
                setKnewtonResponse(response.data.message);
              } catch {
                setKnewtonResponse('Unable to get Knewton analysis right now. Try again later.');
              } finally {
                setKnewtonLoading(false);
              }
            }}
            className="w-full py-2.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-purple-600/20 to-indigo-600/20 text-purple-300 border border-purple-500/30 hover:from-purple-600/30 hover:to-indigo-600/30 hover:text-white transition-all flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" fill="none" />
            </svg>
            Ask Knewton AI
          </button>
        )}
        {knewtonLoading && (
          <div className="glass-card p-3 text-center">
            <div className="flex items-center justify-center gap-2">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
              <span className="text-xs text-purple-300">Knewton is analyzing...</span>
            </div>
          </div>
        )}
        {knewtonResponse && (
          <div className="glass-card p-3 border border-purple-500/20">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" fill="none" />
                </svg>
              </div>
              <span className="text-[10px] font-bold text-purple-300 uppercase tracking-wider">Knewton Analysis</span>
            </div>
            <p className="text-xs text-text-secondary leading-relaxed whitespace-pre-line">{knewtonResponse}</p>
          </div>
        )}
      </div>

      {/* Pick buttons */}
      {onPickWinner && (
        <div className="flex gap-2">
          <button
            onClick={() => { onPickWinner(team1.entry_id); onClose(); }}
            className="flex-1 py-2 rounded-lg text-xs font-semibold bg-accent-blue/10 text-accent-blue border border-accent-blue/30 hover:bg-accent-blue/20 transition-all"
          >
            Pick #{team1.seed} {team1.team_name}
          </button>
          <button
            onClick={() => { onPickWinner(team2.entry_id); onClose(); }}
            className="flex-1 py-2 rounded-lg text-xs font-semibold bg-accent-purple/10 text-accent-purple border border-accent-purple/30 hover:bg-accent-purple/20 transition-all"
          >
            Pick #{team2.seed} {team2.team_name}
          </button>
        </div>
      )}
    </ModalShell>
  );
}

function ModalShell({ onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-bg-primary/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg max-h-[85vh] overflow-y-auto bg-bg-secondary rounded-xl border border-border shadow-2xl p-5 scrollbar-thin">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1 text-text-tertiary hover:text-text-primary transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {children}
      </div>
    </div>
  );
}

function TeamBadge({ team }) {
  return (
    <div className="flex flex-col items-center gap-1">
      {team.logo_url ? (
        <img src={team.logo_url} alt="" className="w-8 h-8 object-contain" />
      ) : (
        <div className="w-8 h-8 rounded-full bg-bg-card-hover flex items-center justify-center">
          <span className="text-xs font-bold text-text-secondary">{team.seed}</span>
        </div>
      )}
      <span className="text-xs font-bold text-text-primary">{team.team_name}</span>
      <span className="text-[10px] text-text-tertiary">#{team.seed} {team.region}</span>
    </div>
  );
}

function StatRow({ label, val1, val2, lowerBetter = false }) {
  const v1 = parseFloat(val1);
  const v2 = parseFloat(val2);
  const v1Better = !isNaN(v1) && !isNaN(v2) && (lowerBetter ? v1 < v2 : v1 > v2);
  const v2Better = !isNaN(v1) && !isNaN(v2) && (lowerBetter ? v2 < v1 : v2 > v1);

  return (
    <div className="flex items-center text-xs">
      <span className={`flex-1 text-right ${v1Better ? 'text-success font-semibold' : 'text-text-secondary'}`}>
        {val1 ?? '---'}
      </span>
      <span className="w-20 text-center text-text-tertiary text-[10px]">{label}</span>
      <span className={`flex-1 text-left ${v2Better ? 'text-success font-semibold' : 'text-text-secondary'}`}>
        {val2 ?? '---'}
      </span>
    </div>
  );
}

function formatStreak(team) {
  if (!team.current_streak) return null;
  const type = team.current_streak_type || (team.current_streak > 0 ? 'W' : 'L');
  return `${type}${Math.abs(team.current_streak)}`;
}

export default MatchupAnalyzerModal;
